/**
 * Transaction Details modal — Phase 5A receipt viewer + L4 bonus surface.
 *
 * Triggered from Owner Recent Activity rows when the event type is
 * `transaction`. Shows the canonical transaction fields, receipt image
 * viewer, and — as of L4 (2026-06-25) — the full L1/L2 stamp
 * decomposition:
 *
 *   Purchase Amount · Auto Stamp (locked) · Bonus Stamp · Final Stamp
 *   + Bonus Adjustments history list (append-only per D3)
 *   + "Adjust Bonus Stamp" action for Brand Owner / Super Admin only.
 *
 * The initial `tx` prop comes from `/api/owner/recent-activity` which
 * L4 extended to include the additive fields. After a successful
 * bonus grant we upgrade the modal's local `tx` state from the L2
 * response payload so the user sees the new adjustment inline without
 * closing the modal.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  X, Camera, Image as ImageIcon, ZoomIn, FileText, Lock, Sparkles, Plus, Gift,
} from 'lucide-react';
import { formatRupiah, formatDateTime } from '@/lib/utils';
import { resolveImageUrl } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { StatusPill } from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import BonusStampModal, { BONUS_SOURCES, canGrantBonus } from '@/components/BonusStampModal';

/** Map an enum value back to its friendly label for display. */
const SOURCE_LABEL = Object.fromEntries(BONUS_SOURCES.map((s) => [s.value, s.label]));

function Row({ label, value, muted }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-white/5 last:border-0">
      <div className="text-xs uppercase tracking-wider text-white/40 font-semibold">
        {label}
      </div>
      <div className={`text-sm ${muted ? 'text-white/60' : 'text-white'} text-right max-w-[60%] break-words`}>
        {value == null || value === '' ? <span className="text-white/30">—</span> : value}
      </div>
    </div>
  );
}

function StampBadge({ label, value, variant = 'plain', locked = false, testID }) {
  const shell = {
    plain:   'border-white/10 bg-black/40 text-white',
    accent:  'border-gold/50 bg-gold/10 text-gold',
    ghost:   'border-white/5  bg-black/30 text-white/60',
  }[variant] || 'border-white/10 bg-black/40 text-white';
  return (
    <div
      className={`flex-1 min-w-0 rounded-2xl border ${shell} px-4 py-3`}
      data-testid={testID}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">
          {label}
        </div>
        {locked ? <Lock size={11} className="opacity-70" /> : null}
      </div>
      <div className="text-2xl font-display font-semibold leading-none">{value}</div>
    </div>
  );
}

function SourcePill({ source }) {
  if (!source) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gold/30 bg-gold/10 text-[10px] uppercase tracking-wider text-gold/90 font-semibold"
      data-testid={`bonus-source-${source}`}
    >
      {SOURCE_LABEL[source] ?? source}
    </span>
  );
}

function BonusAdjustmentRow({ adj }) {
  return (
    <li
      className="rounded-xl border border-white/8 bg-black/30 p-3"
      data-testid="bonus-adjustment-row"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base font-semibold text-gold">
              +{adj.amount}
            </span>
            <SourcePill source={adj.source} />
          </div>
          <div className="text-xs text-white/70 mt-1 break-words">
            {adj.reason ?? '—'}
          </div>
          {adj.notes ? (
            <div className="text-[11px] text-white/45 mt-1 italic break-words">
              {adj.notes}
            </div>
          ) : null}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">
            {adj.operator_role === 'super_admin' ? 'Super Admin' : 'Brand Owner'}
          </div>
          <div className="text-[11px] text-white/55 mt-1">
            {formatDateTime(adj.created_at)}
          </div>
        </div>
      </div>
    </li>
  );
}

export default function TransactionDetailsModal({ tx: initialTx, onClose }) {
  const [enlarged, setEnlarged] = useState(false);
  const [tx, setTx] = useState(initialTx);
  const [bonusOpen, setBonusOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  useEffect(() => {
    // Prop refresh (parent list refetched and passed a newer snapshot).
    setTx(initialTx);
  }, [initialTx]);

  if (!tx) return null;

  const receiptSrc = tx.receipt_url ? resolveImageUrl(tx.receipt_url) : null;

  // Resolve L1/L2 fields with legacy fallbacks. Rows written pre-L1
  // don't carry `auto_stamp` / `bonus_stamp` / `final_stamp`, so we
  // default from `stamps_earned` for the auto row and 0 for bonus.
  const stampsEarned = Number(tx.stamps_earned ?? 0);
  const autoStamp   = tx.auto_stamp   != null ? Number(tx.auto_stamp)   : stampsEarned;
  const bonusStamp  = tx.bonus_stamp  != null ? Number(tx.bonus_stamp)  : 0;
  const finalStamp  = tx.final_stamp  != null ? Number(tx.final_stamp)  : stampsEarned;
  const txnType     = tx.txn_type ?? tx.type_backend ?? tx.type ?? 'PURCHASE';
  // NB: `tx.type` on activity rows means the activity type
  // ('transaction' | 'redemption') NOT the transaction TYPE
  // discriminator. `owner_recent_activity` was extended to expose the
  // real discriminator as `txn_type` for L4.
  const isBonusTxn = txnType === 'BONUS';
  const bonusAdjustments = Array.isArray(tx.bonus_adjustments) ? tx.bonus_adjustments : [];
  // Newest-first sort — server may return append-order which is
  // chronologically ascending; UI wants descending.
  const bonusAdjustmentsSorted = [...bonusAdjustments].sort((a, b) => {
    const ta = a?.created_at || '';
    const tb = b?.created_at || '';
    return tb.localeCompare(ta);
  });

  const showBonusButton = canGrantBonus(user) && !isBonusTxn;
  const shortId = tx.id ? String(tx.id).slice(0, 8) : '';

  const handleGranted = (response) => {
    // Fuse the L2 response into our local tx so the modal shows the
    // fresh state without a close/reopen. Response has the canonical
    // triple + full bonus_adjustments list.
    setTx((prev) => ({
      ...(prev || {}),
      auto_stamp:        response?.auto_stamp   ?? prev?.auto_stamp,
      bonus_stamp:       response?.bonus_stamp  ?? prev?.bonus_stamp,
      final_stamp:       response?.final_stamp  ?? prev?.final_stamp,
      stamps_earned:     response?.stamps_earned ?? prev?.stamps_earned,
      bonus_adjustments: response?.bonus_adjustments ?? prev?.bonus_adjustments,
      version:           response?.version     ?? prev?.version,
      txn_type:          response?.type        ?? prev?.txn_type,
    }));
    // Loose invalidation — the recent-activity list, the CRM tx lists,
    // and the customer overview all need to refetch. `predicate` keeps
    // us decoupled from every specific queryKey factory.
    qc.invalidateQueries({ predicate: (query) => {
      const key = Array.isArray(query.queryKey) ? query.queryKey[0] : '';
      return typeof key === 'string' && (
        key.startsWith('owner-recent-activity')
        || key.startsWith('owner-crm-customer')
        || key.startsWith('admin-crm-customer')
        || key.startsWith('owner-overview')
        || key === 'owner'
      );
    }});
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="tx-details-modal"
      >
        <div
          className="bg-[#15151B] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-white/8">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-xs uppercase tracking-wider text-gold/90 font-semibold">
                  Transaction
                </div>
                {isBonusTxn ? (
                  <StatusPill variant="gold">
                    <span className="inline-flex items-center gap-1">
                      <Gift size={10} /> Bonus
                    </span>
                  </StatusPill>
                ) : (
                  <StatusPill variant="neutral">
                    <span className="inline-flex items-center gap-1">Purchase</span>
                  </StatusPill>
                )}
              </div>
              <div className="font-display text-xl font-semibold text-white mt-0.5 truncate" data-testid="tx-details-customer-name">
                {tx.customer_name ?? '—'}
              </div>
              <div className="text-xs text-white/45 mt-1 font-mono">
                #{shortId} · {formatDateTime(tx.at ?? tx.created_at)}
              </div>
            </div>
            <button
              type="button"
              className="w-9 h-9 rounded-full border border-white/10 hover:bg-white/5 text-white/70 grid place-items-center"
              onClick={onClose}
              data-testid="tx-details-close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Stamp decomposition badges */}
            <div className="grid grid-cols-3 gap-2" data-testid="tx-stamp-decomposition">
              <StampBadge label="Auto" value={autoStamp} locked testID="tx-auto-stamp" />
              <StampBadge label="Bonus" value={bonusStamp} variant="ghost" testID="tx-bonus-stamp" />
              <StampBadge label="Final" value={finalStamp} variant="accent" testID="tx-final-stamp" />
            </div>

            {/* "Adjust Bonus Stamp" button — owner + SA only, hidden on BONUS type */}
            {showBonusButton ? (
              <div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setBonusOpen(true)}
                  testID="tx-adjust-bonus-btn"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Sparkles size={14} /> Adjust Bonus Stamp
                  </span>
                </Button>
              </div>
            ) : null}

            {/* Receipt viewer — skipped for BONUS-type (no receipt) */}
            {!isBonusTxn ? (
              <div data-testid="tx-receipt-viewer">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-gold" />
                    <span className="text-xs uppercase tracking-wider text-white/55 font-semibold">
                      Receipt
                    </span>
                  </div>
                  {tx.receipt_source ? (
                    <StatusPill variant="gold">
                      {tx.receipt_source === 'photo' || tx.receipt_source === 'camera' ? (
                        <span className="inline-flex items-center gap-1">
                          <Camera size={10} />
                          Camera
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <ImageIcon size={10} />
                          Gallery
                        </span>
                      )}
                    </StatusPill>
                  ) : null}
                </div>
                {receiptSrc ? (
                  <button
                    type="button"
                    onClick={() => setEnlarged(true)}
                    className="group relative w-full bg-[#0E0E13] border border-white/10 rounded-xl overflow-hidden block"
                    data-testid="tx-receipt-image"
                  >
                    <img
                      src={receiptSrc}
                      alt="Receipt"
                      className="w-full max-h-80 object-contain"
                    />
                    <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/40 transition-colors">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 text-xs text-white bg-black/80 px-3 py-1.5 rounded-full border border-white/15">
                        <ZoomIn size={12} /> Tap to enlarge
                      </span>
                    </div>
                    {tx.receipt_uploaded_at ? (
                      <div className="absolute bottom-2 left-2 text-[10px] text-white/70 bg-black/70 rounded-md px-2 py-0.5">
                        Uploaded {formatDateTime(tx.receipt_uploaded_at)}
                      </div>
                    ) : null}
                  </button>
                ) : (
                  <div className="border border-dashed border-white/10 rounded-xl py-6 text-center text-xs text-white/40" data-testid="tx-receipt-empty">
                    No receipt was attached to this transaction.
                  </div>
                )}
              </div>
            ) : null}

            {/* Details */}
            <div className="bg-[#0E0E13] border border-white/8 rounded-xl p-4">
              <Row
                label="Purchase Amount"
                value={<span className="font-display text-lg text-gold font-semibold">{formatRupiah(tx.amount_rupiah)}</span>}
                muted={isBonusTxn}
              />
              <Row label="Invoice" value={tx.invoice_number} />
              <Row label="Branch" value={tx.branch_name} />
              <Row label="Customer" value={`${tx.customer_name || '—'}${tx.membership_id ? ` · ${tx.membership_id}` : ''}`} />
              {tx.qr_mode ? (
                <Row
                  label="QR mode"
                  value={tx.qr_mode === 'manual' ? 'Manual lookup' : 'Scan'}
                />
              ) : null}
              {tx.notes ? <Row label="Notes" value={tx.notes} /> : null}
            </div>

            {/* Bonus adjustments — always render the section so an
                empty state exists for zero-bonus transactions. */}
            <div data-testid="tx-bonus-adjustments">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Plus size={14} className="text-gold" />
                  <span className="text-xs uppercase tracking-wider text-white/55 font-semibold">
                    Bonus Adjustments {bonusAdjustmentsSorted.length > 0 ? `· ${bonusAdjustmentsSorted.length}` : ''}
                  </span>
                </div>
                <span className="text-[10px] text-white/35">newest first · immutable</span>
              </div>
              {bonusAdjustmentsSorted.length > 0 ? (
                <ul className="space-y-2" data-testid="tx-bonus-adjustments-list">
                  {bonusAdjustmentsSorted.map((adj) => (
                    <BonusAdjustmentRow key={adj.adjustment_id || adj.created_at} adj={adj} />
                  ))}
                </ul>
              ) : (
                <div
                  className="border border-dashed border-white/10 rounded-xl py-5 text-center text-xs text-white/40"
                  data-testid="tx-bonus-adjustments-empty"
                >
                  No bonus adjustments yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enlarged receipt viewer */}
      {enlarged && receiptSrc ? (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-6"
          onClick={() => setEnlarged(false)}
          data-testid="tx-receipt-fullscreen"
        >
          <button
            type="button"
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
            onClick={() => setEnlarged(false)}
          >
            <X size={18} />
          </button>
          <img
            src={receiptSrc}
            alt="Receipt"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}

      {/* L4 bonus grant modal */}
      <BonusStampModal
        open={bonusOpen}
        onClose={() => setBonusOpen(false)}
        mode="on_transaction"
        context={{
          transactionId: tx.id,
          shortId,
          autoStamp,
          bonusStamp,
        }}
        onGranted={handleGranted}
      />
    </>
  );
}
